import { useEffect, useState } from 'react';
import {
  approveClawback,
  assignCommissionPlan,
  createPartner,
  loadAdminWorkspace,
  loadCommissionPlans,
  mintPartnerCode,
  issueOnboardingReferralCode,
  issuePartnerOffer,
  rejectClawback,
  reviewPartnerApplication,
  runPayoutRoll,
  setOnboardingRequirement,
  setPartnerStatus,
  transitionPartnerProgram,
} from '../admin/repository';
import type { AdminClawback, AdminCommissionPlan, AdminOnboardingApplication, AdminPartner, AdminPayout, AdminWorkspaceData } from '../admin/types';
import type { NavKey, Workspace } from '../types';

const PARTNER_STATUSES = ['invited', 'active', 'paused', 'retired'];

interface Props {
  workspace: Workspace;
  activePage: NavKey;
  writable: boolean;
}

// ---- formatting -------------------------------------------------------------
function usd(cents: number): string {
  const dollars = cents / 100;
  return dollars.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(dollars) ? 0 : 2,
    maximumFractionDigits: 2,
  });
}
function date(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
// The previous calendar month, the natural default payout period.
function lastMonthPeriod(): { start: string; end: string } {
  const now = new Date();
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  return { start: start.toISOString(), end: end.toISOString() };
}

// ---- workspace --------------------------------------------------------------
export function AdminWorkspace({ activePage, writable }: Props) {
  const [data, setData] = useState<AdminWorkspaceData | null>(null);
  const [plans, setPlans] = useState<AdminCommissionPlan[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setLoading(true);
    setError('');
    try {
      const [workspace, planList] = await Promise.all([
        loadAdminWorkspace(),
        // Plans power the assign dropdown; a non-writable viewer doesn't need them.
        writable ? loadCommissionPlans() : Promise.resolve([]),
      ]);
      setData(workspace);
      setPlans(planList);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Administration data could not be loaded.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (loading) return <DashboardSkeleton />;
  if (error) return <DashboardError message={error} retry={refresh} />;
  if (!data) return null;

  switch (activePage) {
    case 'home':
      return <OverviewView data={data} />;
    case 'creator-partners':
      return <PartnersView partners={data.partners} onboarding={data.onboarding} plans={plans} writable={writable} refresh={refresh} />;
    case 'revenue-payouts':
      return <PayoutsView data={data} writable={writable} refresh={refresh} />;
    case 'settings':
      return <SettingsView />;
    case 'help':
      return <HelpView />;
    case 'resources':
      return <ResourcesView />;
    default:
      // programs / cohorts / participants / reports — cross-org CWI admin.
      return <PhaseNote page={activePage} />;
  }
}

// ---- home / overview --------------------------------------------------------
function OverviewView({ data }: { data: AdminWorkspaceData }) {
  const o = data.overview;
  const metrics: [string, string, string][] = [
    ['PARTNERS', o.totalPartners.toLocaleString(), `${o.activePartners.toLocaleString()} active`],
    ['OWED NOW', usd(o.availableCents), 'Available across all partners'],
    ['PAID TO DATE', usd(o.paidCents), 'Across all payouts'],
    ['BUILDERS REFERRED', o.referredBuilders.toLocaleString(), 'Through partner codes'],
    ['CLAWBACKS TO REVIEW', o.pendingClawbacks.toLocaleString(), 'Awaiting an operator'],
  ];
  return (
    <div className="page-grid">
      <section className="page-heading">
        <p className="eyebrow">STAKD ADMINISTRATION</p>
        <h1>Creator partner program</h1>
        <p>The whole partner program at a glance — who's earning, what's owed, and what needs your review.</p>
      </section>
      <section className="metric-grid">
        {metrics.map(([label, value, sub]) => (
          <Metric key={label} label={label} value={value} sub={sub} />
        ))}
      </section>
      <article className="panel">
        <p className="eyebrow">NEEDS ATTENTION</p>
        <h2>Operator queue</h2>
        <div className="health-list">
          <Health label="Clawbacks awaiting review" value={o.pendingClawbacks} level={o.pendingClawbacks ? 'warning' : 'good'} />
          <Health label="Available &amp; unpaid (run a payout)" value={o.availableCents > 0 ? 1 : 0} level={o.availableCents > 0 ? 'warning' : 'good'} />
        </div>
        <p className="muted" style={{ marginTop: 12 }}>Assign plans and change status from Creator Partners; approve or reject clawbacks from Revenue &amp; Payouts. Every action is audited.</p>
      </article>
    </div>
  );
}

// ---- partners ---------------------------------------------------------------
function PartnersView({ partners, onboarding, plans, writable, refresh }: { partners: AdminPartner[]; onboarding: AdminOnboardingApplication[]; plans: AdminCommissionPlan[]; writable: boolean; refresh(): Promise<void> }) {
  const [note, setNote] = useState('');
  const [busyId, setBusyId] = useState('');

  async function run(id: string, work: () => Promise<void>, ok: string) {
    setBusyId(id);
    setNote('');
    try {
      await work();
      setNote(ok);
      await refresh();
    } catch (reason) {
      setNote(reason instanceof Error ? reason.message : 'Action failed.');
    } finally {
      setBusyId('');
    }
  }

  return (
    <div className="page-grid">
      <PageTitle eyebrow="CREATOR PARTNERS" title="Every partner" body="Classification, status, lifetime earnings, and what's available to pay out — across the whole program." />
      <OnboardingQueue applications={onboarding} plans={plans} writable={writable} refresh={refresh} />
      {writable ? <CreatePartnerPanel refresh={refresh} setNote={setNote} /> : null}
      {note ? <article className="panel"><p className="muted">{note}</p></article> : null}
      <article className="panel">
        {partners.length === 0 ? (
          <p className="muted">No partners yet. {writable ? 'Add one with “Create partner” above.' : 'Create one from the staff-gated admin tools.'}</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Handle</th>
                <th>Name</th>
                <th>Class</th>
                <th>Status</th>
                <th className="num">Referred</th>
                <th className="num">Lifetime</th>
                <th className="num">Available</th>
                {writable ? <th>Manage</th> : null}
              </tr>
            </thead>
            <tbody>
              {partners.map((p) => (
                <tr key={p.partnerId}>
                  <td><strong>{p.handle}</strong></td>
                  <td>{p.displayName}</td>
                  <td>{p.tier === 'founding' ? 'Founding' : 'Creator'}</td>
                  <td><span className={`status ${p.status}`}>{p.status}</span></td>
                  <td className="num">{p.referredCount.toLocaleString()}</td>
                  <td className="num">{usd(p.lifetimeCents)}</td>
                  <td className="num">{usd(p.availableCents)}</td>
                  {writable ? (
                    <td>
                      <div className="row-actions">
                        <label className="sr-only" htmlFor={`status-${p.partnerId}`}>Status for {p.handle}</label>
                        <select
                          id={`status-${p.partnerId}`}
                          value={p.status}
                          disabled={busyId === p.partnerId}
                          onChange={(e) => {
                            const next = e.target.value;
                            if (next === p.status) return;
                            void run(p.partnerId, () => setPartnerStatus(p.partnerId, next), `${p.handle} → ${next}.`);
                          }}
                        >
                          {PARTNER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <label className="sr-only" htmlFor={`plan-${p.partnerId}`}>Assign plan to {p.handle}</label>
                        <select
                          id={`plan-${p.partnerId}`}
                          defaultValue=""
                          disabled={busyId === p.partnerId || plans.length === 0}
                          onChange={(e) => {
                            const planVersionId = e.target.value;
                            if (!planVersionId) return;
                            const plan = plans.find((pl) => pl.planVersionId === planVersionId);
                            const label = plan ? `${plan.displayName}` : 'plan';
                            e.target.value = '';
                            if (!window.confirm(`Assign “${label}” to ${p.handle}? This ends the current assignment and appends the new one. Audited.`)) return;
                            void run(p.partnerId, () => assignCommissionPlan(p.partnerId, planVersionId), `${p.handle} assigned ${label}.`);
                          }}
                        >
                          <option value="">Assign plan…</option>
                          {plans.map((pl) => <option key={pl.planVersionId} value={pl.planVersionId}>{pl.displayName}</option>)}
                        </select>
                        <button
                          className="link-button"
                          disabled={busyId === p.partnerId}
                          onClick={() => {
                            const code = window.prompt(`Mint a referral code for ${p.handle} (letters/numbers):`);
                            if (!code || !code.trim()) return;
                            void run(p.partnerId, () => mintPartnerCode(p.partnerId, code.trim().toUpperCase()), `Code ${code.trim().toUpperCase()} minted for ${p.handle}.`);
                          }}
                        >Mint code</button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </article>
    </div>
  );
}

function OnboardingQueue({ applications, plans, writable, refresh }: { applications: AdminOnboardingApplication[]; plans: AdminCommissionPlan[]; writable: boolean; refresh(): Promise<void> }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  async function run(id: string, work: () => Promise<void>, message: string) {
    setBusy(id); setNote('');
    try { await work(); setNote(message); await refresh(); }
    catch (reason) { setNote(reason instanceof Error ? reason.message : 'Onboarding action failed.'); }
    finally { setBusy(''); }
  }
  return <article className="panel table-panel">
    <div className="section-heading"><div><p className="eyebrow">ONBOARDING PIPELINE</p><h2>Fit review to referral-ready</h2></div><span>{applications.length} open</span></div>
    {note ? <p className="muted" style={{ padding: '0 24px' }}>{note}</p> : null}
    {applications.length === 0 ? <p className="muted" style={{ padding: 24 }}>No applications are waiting.</p> : <div className="table-scroll"><table><thead><tr><th>Applicant</th><th>Stage</th><th>SLA</th><th>Gate</th>{writable ? <th>Next action</th> : null}</tr></thead><tbody>
      {applications.map((a) => <tr key={a.applicationId}><td><strong>{a.displayName}</strong><small>{a.email}</small></td><td><span className={`status ${a.status}`}>{a.currentStage.replace(/_/g, ' ')}</span><small>{a.ownerRole.replace(/_/g, ' ')}</small></td><td className={a.slaOverdue ? 'danger' : ''}>{a.slaDueAt ? `${a.slaOverdue ? 'Overdue · ' : ''}${date(a.slaDueAt)}` : 'No clock'}</td><td>{a.missingRequirements.length ? `${a.missingRequirements.length} remaining` : 'Ready'}</td>{writable ? <td><div className="row-actions">
        {a.status === 'under_review' ? <><button className="link-button" disabled={busy===a.applicationId} onClick={() => void run(a.applicationId,()=>reviewPartnerApplication(a.applicationId,'approve'),'Application approved.')}>Approve</button><button className="link-button danger" disabled={busy===a.applicationId} onClick={() => { const reason=window.prompt('Rejection reason:'); if(reason?.trim()) void run(a.applicationId,()=>reviewPartnerApplication(a.applicationId,'reject',reason.trim()),'Application rejected.'); }}>Reject</button></> : null}
        {a.status === 'approved' ? <button className="link-button" disabled={busy===a.applicationId||!plans.length} onClick={() => { const planId=window.prompt(`Approved plan version id:\n${plans.map(p=>`${p.displayName}: ${p.planVersionId}`).join('\n')}`); const summary=window.prompt('Personalized Offer Summary (rates remain sourced from the selected plan):'); if(planId?.trim()&&summary?.trim()) void run(a.applicationId,()=>issuePartnerOffer(a.applicationId,planId.trim(),summary.trim()),'Offer Summary issued.'); }}>Issue offer</button> : null}
        {a.partnerId && a.status === 'onboarding' && a.missingRequirements.length ? <button className="link-button" disabled={busy===a.applicationId} onClick={() => { const requirement=a.missingRequirements[0]; if(window.confirm(`Mark “${requirement.label}” complete? This is audited.`)) void run(a.applicationId,()=>setOnboardingRequirement(a.partnerId!,requirement.key,'complete'),`${requirement.label} completed.`); }}>Complete next</button> : null}
        {a.partnerId && a.status === 'onboarding' && !a.missingRequirements.length ? <button className="link-button" disabled={busy===a.applicationId} onClick={() => { const code=window.prompt('Referral code:'); if(code?.trim()) void run(a.applicationId,async()=>{ await issueOnboardingReferralCode(a.partnerId!,code.trim()); await transitionPartnerProgram(a.partnerId!,'active','Onboarding definition of done met'); },'Referral code issued and Creator Partner activated.'); }}>Issue code + activate</button> : null}
      </div></td> : null}</tr>)}
    </tbody></table></div>}
  </article>;
}

function CreatePartnerPanel({ refresh, setNote }: { refresh(): Promise<void>; setNote(v: string): void }) {
  const [open, setOpen] = useState(false);
  const [handle, setHandle] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [tier, setTier] = useState<'partner' | 'founding'>('partner');
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!handle.trim() || !displayName.trim() || !email.includes('@')) {
      setNote('A handle, display name, and valid email are required.');
      return;
    }
    setBusy(true);
    setNote('');
    try {
      await createPartner({ handle: handle.trim(), displayName: displayName.trim(), email: email.trim(), tier });
      setNote(`Partner ${handle.trim()} created.`);
      setHandle(''); setDisplayName(''); setEmail(''); setTier('partner'); setOpen(false);
      await refresh();
    } catch (reason) {
      setNote(reason instanceof Error ? reason.message : 'Could not create the partner.');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div className="title-actions">
        <p className="muted">Onboard a creator partner, then mint their referral code.</p>
        <button className="primary-button compact" onClick={() => setOpen(true)}>Create partner</button>
      </div>
    );
  }

  return (
    <article className="panel">
      <div className="section-heading"><div><p className="eyebrow">NEW PARTNER</p><h2>Create a creator partner</h2></div></div>
      <div className="admin-form">
        <label>Handle<input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="e.g. ACME" /></label>
        <label>Display name<input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Acme Media" /></label>
        <label>Email<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="partner@example.com" /></label>
        <label>Class
          <select value={tier} onChange={(e) => setTier(e.target.value as 'partner' | 'founding')}>
            <option value="partner">Creator</option>
            <option value="founding">Founding</option>
          </select>
        </label>
        <div className="form-actions">
          <button className="primary-button compact" onClick={() => void submit()} disabled={busy}>{busy ? 'Creating…' : 'Create'}</button>
          <button className="link-button" onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
        </div>
      </div>
    </article>
  );
}

// ---- revenue & payouts ------------------------------------------------------
function PayoutsView({ data, writable, refresh }: { data: AdminWorkspaceData; writable: boolean; refresh(): Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [clawbackBusyId, setClawbackBusyId] = useState('');

  async function runClawback(id: string, work: () => Promise<void>, ok: string) {
    setClawbackBusyId(id);
    setNote('');
    try {
      await work();
      setNote(ok);
      await refresh();
    } catch (reason) {
      setNote(reason instanceof Error ? reason.message : 'The clawback decision failed.');
    } finally {
      setClawbackBusyId('');
    }
  }

  async function onRunPayout() {
    const period = lastMonthPeriod();
    const label = `${date(period.start)} → ${date(period.end)}`;
    if (!window.confirm(`Run the payout roll for ${label}? This creates payout batches for every partner over their plan minimum. Audited.`)) return;
    setBusy(true);
    setNote('');
    try {
      await runPayoutRoll(period.start, period.end);
      setNote(`Payout roll completed for ${label}.`);
      await refresh();
    } catch (reason) {
      setNote(reason instanceof Error ? reason.message : 'The payout roll failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-grid">
      <div className="title-actions">
        <PageTitle eyebrow="REVENUE &amp; PAYOUTS" title="Payouts &amp; clawbacks" body="Run the monthly payout roll and review reversals. Every payout-affecting action is audited." />
        {writable ? (
          <button className="primary-button compact" onClick={onRunPayout} disabled={busy}>
            {busy ? 'Running…' : 'Run payout roll'}
          </button>
        ) : null}
      </div>
      {note ? <article className="panel"><p className="muted">{note}</p></article> : null}

      <article className="panel">
        <div className="section-heading">
          <div>
            <p className="eyebrow">CLAWBACKS</p>
            <h2>Awaiting review</h2>
          </div>
          <span>{data.clawbacks.length} open</span>
        </div>
        {data.clawbacks.length === 0 ? (
          <p className="muted">No clawbacks awaiting review. Refund/chargeback reversals auto-apply; fraud, contractual, and manual corrections queue here for an operator decision.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Partner</th>
                <th>Type</th>
                <th className="num">Amount</th>
                <th>Status</th>
                <th>Dispute until</th>
                <th>Reason</th>
                {writable ? <th>Decision</th> : null}
              </tr>
            </thead>
            <tbody>
              {data.clawbacks.map((c: AdminClawback) => (
                <tr key={c.id}>
                  <td><strong>{c.handle}</strong></td>
                  <td>{c.eventType.replace(/_/g, ' ')}</td>
                  <td className="num">{c.appliedPartnerEarningCents == null ? '—' : usd(c.appliedPartnerEarningCents)}</td>
                  <td><span className={`status ${c.status}`}>{c.status.replace(/_/g, ' ')}</span></td>
                  <td>{date(c.disputeUntil)}</td>
                  <td>{c.reason}</td>
                  {writable ? (
                    <td>
                      {c.status === 'pending_review' || c.status === 'disputed' ? (
                        <div className="row-actions">
                          <button
                            className="link-button danger"
                            disabled={clawbackBusyId === c.id}
                            onClick={() => {
                              if (!window.confirm(`Approve this clawback against ${c.handle}? It reverses their earning on the linked transaction. Audited and immutable.`)) return;
                              void runClawback(c.id, () => approveClawback(c.id), `Clawback approved for ${c.handle}.`);
                            }}
                          >Approve</button>
                          <button
                            className="link-button"
                            disabled={clawbackBusyId === c.id}
                            onClick={() => {
                              const reason = window.prompt(`Reject this clawback for ${c.handle}? Give a reason (kept for audit):`);
                              if (!reason || !reason.trim()) return;
                              void runClawback(c.id, () => rejectClawback(c.id, reason.trim()), `Clawback rejected for ${c.handle}.`);
                            }}
                          >Reject</button>
                        </div>
                      ) : <span className="muted">—</span>}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </article>

      <article className="panel">
        <div className="section-heading">
          <div>
            <p className="eyebrow">PAYOUT BATCHES</p>
            <h2>Recent rolls</h2>
          </div>
          <span>{usd(data.overview.paidCents)} paid to date</span>
        </div>
        {data.payouts.length === 0 ? (
          <p className="muted">No payout batches yet.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Partner</th>
                <th>Period</th>
                <th className="num">Amount</th>
                <th>Status</th>
                <th>Issued</th>
              </tr>
            </thead>
            <tbody>
              {data.payouts.map((p: AdminPayout) => (
                <tr key={p.id}>
                  <td><strong>{p.handle}</strong></td>
                  <td>{date(p.periodStart)} → {date(p.periodEnd)}</td>
                  <td className="num">{usd(p.amountCents)}</td>
                  <td><span className={`status ${p.status}`}>{p.status}</span></td>
                  <td>{date(p.issuedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </article>
    </div>
  );
}

// ---- static pages -----------------------------------------------------------
function SettingsView() {
  return (
    <div className="page-grid">
      <PageTitle eyebrow="SETTINGS" title="Administration" body="Global staff roles and dual-approval controls arrive with Phase C. Today, staff access is a single allow-list." />
      <section className="foundation-grid">
        <article className="panel"><h2>Access</h2><p>Staff membership is an explicit allow-list (`staff_users`). Granular roles — Finance, Partner Ops, Compliance, Auditor — land in Phase C.</p></article>
        <article className="panel"><h2>Audit</h2><p>Every payout-affecting action writes to an immutable `admin_audit` trail.</p></article>
        <article className="panel"><h2>Privacy</h2><p>Sponsor reports use the configured k-anonymity minimum; creator views are aggregate-only. No service-role key ever reaches the browser.</p></article>
      </section>
    </div>
  );
}
function ResourcesView() {
  return (
    <div className="page-grid">
      <PageTitle eyebrow="RESOURCES" title="Operator resources" body="How the program is run." />
      <section className="foundation-grid">
        <article className="panel"><h2>Commission</h2><p>Approved, versioned governance plans define rates, eligible products, holds, and payout minimums. Historical earnings retain their resolved rule.</p></article>
        <article className="panel"><h2>Clawbacks</h2><p>Refunds/chargebacks auto-apply; fraud/contractual/manual queue for approval. An ordinary cancellation is never clawed back.</p></article>
        <article className="panel"><h2>Payouts</h2><p>Payout minimums and holds resolve from each Creator Partner's assigned plan before an amount becomes available.</p></article>
      </section>
    </div>
  );
}
function HelpView() {
  return (
    <div className="page-grid">
      <PageTitle eyebrow="HELP" title="Administration support" body="Operating the STAKD partner program." />
      <section className="foundation-grid">
        <article className="panel"><h2>Run a payout</h2><p>Revenue &amp; Payouts → Run payout roll. It batches every partner over their plan minimum for the period, and is audited.</p></article>
        <article className="panel"><h2>Review a clawback</h2><p>Open reversals show under Revenue &amp; Payouts. Approve to reverse the earning on the linked transaction, or reject to leave it with the partner — both audited.</p></article>
        <article className="panel"><h2>Add a partner</h2><p>Creator Partners → Create partner, then Mint code. Set status and assign a commission plan from the same row.</p></article>
      </section>
    </div>
  );
}
function PhaseNote({ page }: { page: NavKey }) {
  return (
    <div className="page-grid">
      <section className="panel empty-state">
        <p className="eyebrow">STAKD ADMINISTRATION</p>
        <h1>{page.replace(/-/g, ' ')}</h1>
        <p>Cross-organization CWI administration lives in each sponsor's own workspace today. A program-wide admin view lands in a later phase.</p>
      </section>
    </div>
  );
}

// ---- shared -----------------------------------------------------------------
function Metric({ label, value, sub }: { label: string; value: string; sub: string }) {
  return <article className="metric-card"><span>{label}</span><strong>{value}</strong><small>{sub}</small></article>;
}
function Health({ label, value, level }: { label: string; value: number; level: string }) {
  return <div className={`health-row ${level}`}><i /><span>{label}</span><strong>{value}</strong></div>;
}
function PageTitle({ eyebrow, title, body }: { eyebrow: string; title: string; body: string }) {
  return <section className="page-heading"><p className="eyebrow">{eyebrow}</p><h1>{title}</h1><p>{body}</p></section>;
}
function DashboardSkeleton() {
  return <div className="page-grid"><div className="skeleton wide" /><div className="metric-grid">{Array.from({ length: 5 }, (_, index) => <div className="skeleton card" key={index} />)}</div><div className="skeleton table" /></div>;
}
function DashboardError({ message, retry }: { message: string; retry(): Promise<void> }) {
  return <div className="page-grid"><section className="panel empty-state"><p className="eyebrow">DATA UNAVAILABLE</p><h1>Administration could not load</h1><div className="error">{message}</div><button className="primary-button compact" onClick={() => void retry()}>Try again</button></section></div>;
}
